import { Component, Input, OnInit, ViewChild, ElementRef, HostListener } from '@angular/core';
import * as moment from 'moment';
import { SettingsService } from '../../api/resources/settings/settings.service';
import { ActivatedRoute, Router } from '@angular/router';
import { AgentCalendar } from '../../api/model/agent-calendar';
import { AgentCalendarProductEventGender } from '../../api/model/agent-calendar-product-event-gender';
import { AgentCalendarProduct } from '../../api/model/agent-calendar-product';
import { StaticUtils } from '../../utils/static-utils';
import { Event } from '../../api/model/event';
import { AgentCalendarProductEvent } from '../../api/model/agent-calendar-product-event';

@Component({
  selector: 'app-day-product-events-card',
  templateUrl: './day-product-events-card.component.html',
  styleUrls: ['./day-product-events-card.component.scss']
})
export class DayProductEventsCardComponent implements OnInit {
  @Input('partnerId') partnerId: number;
  @Input('agentCalendar') agentCalendar: AgentCalendar;
  utils = StaticUtils;
  agentCalendarGenderPricesByProduct: any = {};
  agentCalendarPricePerGroup: any = {};
  moment = moment;
  openRow: { productIdx: number; eventIdx: number } | null = null;
  selectedOptions: { [key: string]: any } = {};

  constructor(
    private _settingsService: SettingsService,
    private _route: ActivatedRoute,
    private _router: Router) { }

  ngOnInit() {
    this.agentCalendarGenderPricesByProduct = this.getAgentCalendarEventsGenderPricesByProduct(this.agentCalendar.products);
    this.initializeDefaultHashes();
  }

  public Array = Array;

  private initializeDefaultHashes(): void {
    if (!this.agentCalendar || !this.agentCalendar.products) {
      return;
    }

    this.agentCalendar.products.forEach((product, pIdx) => {
      if (!product.events) {
        return;
      }
      product.events.forEach((event: any, j: number) => {
        // Only for those who use tier (format == 2) and don't have a defined hash yet
        if (event.passengerSettings && event.passengerSettings.format === 2) {
          const opts = this.getGenderPricesOptions(event);
          if (opts && opts.length) {
            const key = `${pIdx}-${j}`;
            // Only defines if it doesn't exist
            if (!this.selectedOptions[key]) {
              this.selectedOptions[key] = opts[0].hash;
            }
          }
        }
      });
    });
  }

  toggleNotifications(productIdx: number, eventIdx: number): void {
    const alreadyOpen =
      this.openRow &&
      this.openRow.productIdx === productIdx &&
      this.openRow.eventIdx === eventIdx;

    this.openRow = alreadyOpen ? null : { productIdx, eventIdx };
  }

  getGenderPricesOptions(event: Event): any {
    return event.genders.map(gp => ({
      label: gp.audienceDescription,
      value: gp.audienceType,
      hash: gp.hashedAudience
    }));
  }

  onSelectGenderOption(option: any, pIdx: number, j: number) {
    const key = `${pIdx}-${j}`;
    this.selectedOptions[key] = option;
  }

  getTierPrices(pIdx: number, j: number, productId: number): any[] {
    const key = `${pIdx}-${j}`;
    const opt = this.selectedOptions[key];
    if (!opt) return [];
    const groupStruct = this.agentCalendarGenderPricesByProduct[productId][j] as any;

    return groupStruct.tierPrices[opt] || [];
  }

  isOpen(productIdx: number, eventIdx: number): boolean {
    return !!this.openRow &&
      this.openRow.productIdx === productIdx &&
      this.openRow.eventIdx === eventIdx;
  }

  getAgentCalendarEventsGenderPricesByProduct(products: AgentCalendarProduct[]): any {
    const productsGenderPrices: any = {};

    if (!products || products.length === 0) {
      return productsGenderPrices;
    }

    for (const product of products) {
      const finalStructure: any[] = [];

      for (const event of product.events) {
        if (event.genders && event.genders.length > 0) {
          if (event.genders[0].genderPriceType !== 3) {
            finalStructure.push(event.genders);
          } else {
            const eventGroupStructure: { genderPrice: any, perGroupPrices: any[], tierPrices: { [key: string]: any[] } } = {
              genderPrice: event.genders[0],
              perGroupPrices: [],
              tierPrices: {}
            };

            if (event.passengerSettings && event.passengerSettings.configurations && event.passengerSettings.format !== 2) {
              eventGroupStructure.perGroupPrices = this.limitByAvailability(event.passengerSettings.configurations, event.quantityAvailable);
            } else if (event.passengerSettings && event.passengerSettings.tieredConfigurations) {
              event.genders.forEach(gp => {
                const tiers = event.passengerSettings.tieredConfigurations[gp.hashedAudience] || [];
                eventGroupStructure.tierPrices[gp.hashedAudience] = this.limitByAvailability(tiers, event.quantityAvailable);
              });
            }

            finalStructure.push(eventGroupStructure);
          }
        }
      }

      productsGenderPrices[product.productId] = finalStructure;
    }

    return productsGenderPrices;
  }

  private limitByAvailability<T extends { upperBound: number | null }>(items: T[], max: number): T[] {
    const result: T[] = [];
    let hitLimit = false;

    for (const item of items) {
      if (!hitLimit && item.upperBound !== null && item.upperBound < max) {
        result.push(Object.assign({}, item));
      } else if (!hitLimit) {
        result.push(Object.assign({}, item, { upperBound: max }));
        hitLimit = true;
      }
    }

    // if never hit the limit, make sure the last bound is the one available
    if (!hitLimit && result.length > 0) {
      const last = result[result.length - 1];
      if (last.upperBound !== max) {
        result[result.length - 1] = Object.assign({}, last, { upperBound: max });
      }
    }

    return result;
  }

  goToBook(eventId: number) {
    this._router.navigateByUrl('/partner/' + this.partnerId + '/book/' + eventId);
  }

  goToProductDetail(product: any) {
    if (product && product.productId) {
      this._router.navigateByUrl('partner/' + this.partnerId + /product-detail/ + product.productId);
    }
  }

  getEventResourcesDescription(event: Event): string {
    const result: any = [];

    for (const resource of event.resources) {
      result.push(resource.name);
    }

    return result.length > 0 ? result.join(', ') : ' - ';
  }

  getEventAvailabilityDescription(event: Event): string {
    let result;
    switch (event.quantityAvailable) {
      case 0:
        result = 'Occupied';
        break;
      case -1:
        result = 'Unlimited';
        break;
      default:
        result = 'Available';
    }
    return result;
  }

  isEventBookingDisabled(event: AgentCalendarProductEvent): boolean {
    if (event.isSellableTillEnd) return moment(event.dateEnd).isBefore(moment()) || event.quantityAvailable === 0;
    return moment(event.dateStart).isBefore(moment()) || event.quantityAvailable === 0;
  }

  getGroupType(format: number): string {
    const formatType = {
      0: 'Group',
      1: 'Person',
      2: 'Category Tier'
    }

    return formatType[format];
  }
}



// WEBPACK FOOTER //
// ./src/app/shared/atoms/day-product-events-card/day-product-events-card.component.ts